import { IPet } from '@/shared/interfaces';
import {
    Button,
    HorizontalContainer,
    Input,
    Modal,
    Select,
    VerticalContainer,
} from '@/components';
import { R } from '@/features/profile/resources/ProfileResources.resx';
import { R as Resources } from '@/Resources.resx';
import { PetSexEnum, PetTypeEnum } from '@/shared/enums';
import { Utils } from '@/shared/Utils';
import { useForm } from 'react-hook-form';
import { months } from '@/shared/helpers/months';
import { FormControl } from '@/caregiverSpace/shared/ui';
import { usePetModalMutation } from '@/features/profile/widgets/pets/lib/usePetModalMutation';

export type PetModalProps = {
    pet: IPet;
    onClose: (reload: boolean) => void;
};

type IFormPet = IPet & {
    BirthMonth: string;
    BirthYear: string;
};

const petTypeOptions = [
    { value: PetTypeEnum.Dog, label: R.PetTypeDog },
    { value: PetTypeEnum.Cat, label: R.PetTypeCat },
    { value: PetTypeEnum.Rabbit, label: R.PetTypeRabbit },
    { value: PetTypeEnum.Rodent, label: R.PetTypeRodent },
];

const petSexOptions = [
    { value: PetSexEnum.Female, label: R.PetSexFemale },
    { value: PetSexEnum.FemaleSterilized, label: R.PetSexFemaleSterilized },
    { value: PetSexEnum.Male, label: R.PetSexMale },
    { value: PetSexEnum.MaleNeutered, label: R.PetSexMaleNeutered },
    { value: PetSexEnum.Unknown, label: R.PetSexUnknown },
];

const currentYear = new Date().getFullYear();
const maxYears = currentYear - 30;

const years = [
    ...Array.from(
        { length: currentYear - maxYears + 1 },
        (_, i) => maxYears + i,
    ).map((year) => ({ value: year, label: year.toString() })),
];

function formatMonth(date: Date): string {
    return new Date(date).getMonth().toString();
}

function formatYear(date: Date): string {
    return new Date(date).getFullYear().toString();
}

export const PetModal = ({ onClose, pet }: PetModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        trigger,
    } = useForm<IFormPet>({
        mode: 'onBlur',
        defaultValues: {
            ...(pet ?? {}),
            BirthMonth: pet?.Birthdate ? formatMonth(pet.Birthdate) : '',
            BirthYear: pet?.Birthdate ? formatYear(pet.Birthdate) : '',
        },
    });

    const isEditing = !!pet;

    const updateBirthdate = () => {
        const month = getValues('BirthMonth');
        const year = getValues('BirthYear');

        if (month && year) {
            setValue('Birthdate', new Date(Number(year), Number(month)));
        }
    };

    const { onSubmit, isPending } = usePetModalMutation({
        pet,
        onSuccess: () => onClose(true),
    });

    const onCancel = () => onClose(false);

    return (
        <Modal onClose={onCancel} title={isEditing ? R.EditPet : R.AddPet}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <VerticalContainer
                    spacing="lg"
                    className="h-full justify-between"
                >
                    <main>
                        <VerticalContainer
                            className="justify-between"
                            spacing="md"
                        >
                            <FormControl
                                label={R.PetName}
                                error={errors.Name?.message}
                            >
                                <Input
                                    {...register('Name', {
                                        required: R.PleaseEnterPetName,
                                    })}
                                />
                            </FormControl>

                            <HorizontalContainer
                                className="justify-between"
                                spacing="xs"
                            >
                                <FormControl
                                    error={errors.Type?.message}
                                    label={R.PetType}
                                    className="w-1/2"
                                >
                                    <Select
                                        prompt={R.SelectPetType}
                                        options={petTypeOptions}
                                        defaultValue={pet?.Type ?? ''}
                                        {...register('Type', {
                                            required: R.PleaseSelectPetType,
                                        })}
                                        onChange={(e) =>
                                            setValue(
                                                'Type',
                                                Number(e.target.value),
                                                { shouldValidate: true },
                                            )
                                        }
                                    />
                                </FormControl>

                                <FormControl
                                    error={errors.Sex?.message}
                                    label={R.PetSex}
                                    className="w-1/2"
                                >
                                    <Select
                                        prompt={R.SelectPetSex}
                                        options={petSexOptions}
                                        defaultValue={pet?.Sex ?? ''}
                                        {...register('Sex', {
                                            required: R.PleaseSelectPetSex,
                                        })}
                                        onChange={(e) =>
                                            setValue(
                                                'Sex',
                                                Number(e.target.value),
                                                { shouldValidate: true },
                                            )
                                        }
                                    />
                                </FormControl>
                            </HorizontalContainer>

                            <FormControl
                                label={R.PetBirthdate}
                                spoiler={R.PetBirthdateHint}
                            >
                                <HorizontalContainer
                                    className="justify-between"
                                    spacing="xs"
                                >
                                    <FormControl
                                        error={errors.BirthMonth?.message}
                                        className="w-1/2"
                                    >
                                        <Select
                                            className="w-full"
                                            prompt={R.SelectMonth}
                                            options={months}
                                            defaultValue={
                                                pet
                                                    ? formatMonth(pet.Birthdate)
                                                    : ''
                                            }
                                            {...register('BirthMonth', {
                                                validate: (value) => {
                                                    if (!value)
                                                        return R.PleaseSelectMonth;

                                                    const date = new Date(
                                                        Number(
                                                            getValues(
                                                                'BirthYear',
                                                            ),
                                                        ),
                                                        Number(value),
                                                    );
                                                    return (
                                                        date <= new Date() ||
                                                        Resources.ErrorDateInTheFuture
                                                    );
                                                },
                                            })}
                                            onChange={(e) => {
                                                setValue(
                                                    'BirthMonth',
                                                    e.target.value,
                                                    {
                                                        shouldValidate: true,
                                                    },
                                                );
                                                updateBirthdate();
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl
                                        className="w-1/2"
                                        error={errors.BirthYear?.message}
                                    >
                                        <Select
                                            className="w-full"
                                            prompt={R.SelectYear}
                                            options={years}
                                            defaultValue={
                                                pet
                                                    ? formatYear(pet.Birthdate)
                                                    : ''
                                            }
                                            {...register('BirthYear', {
                                                validate: (value) => {
                                                    if (!value)
                                                        return R.PleaseSelectYear;

                                                    const date = new Date(
                                                        Number(value),
                                                        Number(
                                                            getValues(
                                                                'BirthMonth',
                                                            ),
                                                        ),
                                                    );
                                                    return (
                                                        date <= new Date() || ''
                                                    );
                                                },
                                            })}
                                            onChange={(e) => {
                                                setValue(
                                                    'BirthYear',
                                                    e.target.value,
                                                );
                                                trigger([
                                                    'BirthYear',
                                                    'BirthMonth',
                                                ]);
                                                updateBirthdate();
                                            }}
                                        />
                                    </FormControl>
                                </HorizontalContainer>
                            </FormControl>

                            <FormControl
                                label={R.PetRace}
                                error={errors.Race?.message}
                                spoiler={R.PetRaceHint}
                            >
                                <Input
                                    name={R.PetRace}
                                    {...register('Race', {
                                        required: R.PleaseEnterPetRace,
                                    })}
                                    placeholder={R.PetRacePlaceholder}
                                />
                            </FormControl>

                            <FormControl
                                label={R.PetWeight}
                                error={errors.WeightKg?.message}
                            >
                                <Input
                                    name={R.PetWeight}
                                    {...register('WeightKg', {
                                        valueAsNumber: false,
                                        setValueAs: Utils.normalizeNumber,
                                        validate: (value) =>
                                            Utils.isNumberOrEmpty(
                                                value?.toString(),
                                            ) || R.PleaseEnterANumber,
                                    })}
                                    placeholder={R.PetWeightPlaceholder}
                                />
                            </FormControl>
                        </VerticalContainer>
                    </main>

                    <footer className="text-right">
                        <Button
                            variant="primary"
                            type="submit"
                            className={isPending ? 'button--loading' : ''}
                        >
                            {isEditing ? R.ConfirmEditPet : R.ConfirmAddPet}
                        </Button>
                    </footer>
                </VerticalContainer>
            </form>
        </Modal>
    );
};
